import { call, put, select, takeLatest } from 'redux-saga/effects';

import { ApiResponse } from '../../api/callApi.js';
import * as recommendationsApi from '../../api/recommendations.js';
import {
  AcceptRecommendationType,
  GetRecommendationListType,
  RecommendationPublishParams,
  RecommendationsStateResponse,
  RejectRecommendationType,
} from '../../api/recommendations.js';
import { Action } from '../../types/action.js';
import {
  RecommendationResponse,
  RecommendationType,
} from '../../types/Recommendations.js';
import {
  acceptRecommendationActions,
  publishRecommendationActions,
  recommendationActions,
  recommendationsActions,
  recommendationsStateActions,
  rejectRecommendationActions,
} from '../action/api/RecommendationsApi.js';

function* getRecommendations(
  action: Action<GetRecommendationListType>,
): Generator<any> {
  const response = yield call(
    recommendationsApi.getRecommendations,
    action.payload,
  );

  const { success, data } = response as ApiResponse<RecommendationResponse>;

  if (success) {
    yield put(recommendationsActions.success(data));
  } else {
    yield put(recommendationsActions.failed(data));
  }
}

function* getRecommendationsState(action: Action<number>): Generator<any> {
  const response = yield call(
    recommendationsApi.getRecommendationsState,
    action.payload!,
  );

  const { success, data } =
    response as ApiResponse<RecommendationsStateResponse>;

  if (success) {
    yield put(recommendationsStateActions.success(data));
  } else {
    yield put(recommendationsStateActions.failed(data));
  }
}

function* getRecommendation(action: Action<number>): Generator<any> {
  const response = yield call(
    recommendationsApi.getRecommendation,
    action.payload!,
  );

  const { success, data } = response as ApiResponse<RecommendationResponse>;

  if (success) {
    yield put(recommendationActions.success(data));
  } else {
    yield put(recommendationActions.failed(data));
  }
}

function* acceptRecommendation(
  action: Action<AcceptRecommendationType>,
): Generator<any> {
  const response = yield call(
    recommendationsApi.acceptRecommendation,
    action.payload!,
  );

  const { success, data } = response as ApiResponse<RecommendationType | null>;

  if (success) {
    yield put(acceptRecommendationActions.success(data));
  } else {
    yield put(
      acceptRecommendationActions.failed(
        'Something went wrong when you accepted the suggestion.',
      ),
    );
  }
}

function* rejectRecommendation(
  action: Action<RejectRecommendationType>,
): Generator<any> {
  const response = yield call(
    recommendationsApi.rejectRecommendation,
    action.payload!,
  );

  const { success, data } = response as ApiResponse<RecommendationType | null>;

  if (success) {
    yield put(rejectRecommendationActions.success(data));
  } else {
    yield put(
      rejectRecommendationActions.failed(
        'Something went wrong when you rejected the recommendation.',
      ),
    );
  }
}

function* publishRecommendation(
  action: Required<Action<RecommendationPublishParams>>,
): Generator<any> {
  const response = yield call(
    recommendationsApi.setRecommendationStatus,
    action.payload!,
  );

  const reco = (yield select((state) => {
    return state.recommendations.items.find(
      (el: RecommendationType) => el.id === action.payload.recommendationId,
    );
  })) as RecommendationType;

  const { success, data: newRecoInfo } =
    response as ApiResponse<RecommendationType | null>;

  if (success) {
    yield put(
      publishRecommendationActions.success({
        ...reco,
        ...newRecoInfo,
      }),
    );
  } else {
    yield put(publishRecommendationActions.failed(reco));
  }
}

export default function* recommendationsSaga() {
  yield takeLatest(recommendationsActions.type.REQUEST, getRecommendations);
  yield takeLatest(recommendationActions.type.REQUEST, getRecommendation);
  yield takeLatest(
    recommendationsStateActions.type.REQUEST,
    getRecommendationsState,
  );
  yield takeLatest(
    acceptRecommendationActions.type.REQUEST,
    acceptRecommendation,
  );
  yield takeLatest(
    rejectRecommendationActions.type.REQUEST,
    rejectRecommendation,
  );

  yield takeLatest(
    publishRecommendationActions.type.REQUEST,
    publishRecommendation,
  );
}
